import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgIf } from '@angular/common';
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxMaskDirective } from 'ngx-mask';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs/operators';
import { RecoveryCodesResponse, UserSettingsService } from '../../../../services/members/user-settings.service';
import { GrowlerService } from '../../../shared/growler/growler.service';
import { ControlMessagesComponent } from '../../../shared/control-messages/control-messages.component';
import { OtpCodeInputComponent } from '../otp-code-input/otp-code-input.component';
import { FlexSpacerDirective } from '../directives/flex-spacer.directive';
import { RecoveryCodesPanelComponent } from '../recovery-codes-panel/recovery-codes-panel.component';
import { PHONE_VALIDATION_MASK } from 'app/directives/validators/string/phone-validator.directive';
import { LegfiPhonePipe } from 'app/pipes/format/legfi-phone.pipe';

interface SmsPhoneForm
{
    phone: FormControl<string>;
}

/**
 * SMS enrollment as a self-contained, dialog-agnostic step. Two internal stages: enter a
 * phone number (`startSmsAuth` sends a code), then verify the 6-digit code via the shared OTP
 * control (`finishSmsSetup`), then a show-once recovery-codes stage when the server issues them.
 * Emits `completed` once enrolled and `cancelled` on back-out from the phone stage. No MatDialogRef dependency (SD-8102).
 */
@UntilDestroy()
@Component({
    selector: 'app-sms-auth-setup-step',
    templateUrl: './sms-auth-setup-step.component.html',
    styleUrls: ['./two-factor-setup-step.shared.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgIf, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatProgressSpinnerModule, NgxMaskDirective, ControlMessagesComponent, OtpCodeInputComponent, FlexSpacerDirective, LegfiPhonePipe, RecoveryCodesPanelComponent],
})
export class SmsAuthSetupStepComponent implements OnInit, OnDestroy
{
    /**
     * Label for the first-stage dismiss button. Defaults to 'Cancel' (the step is opened directly,
     * so backing out closes it). The gate dialog opens the step from a method chooser, so it passes
     * 'Back' — the dismiss returns to that chooser rather than closing the flow.
     */
    @Input() dismissLabel = 'Cancel';

    @Output() completed = new EventEmitter<void>();
    @Output() cancelled = new EventEmitter<void>();

    /**
     * Fires when the step reaches its show-once recovery-codes stage, so the host can swap its
     * header — the codes render inside the host's surface, not a dialog of their own.
     */
    @Output() showingRecoveryCodes = new EventEmitter<void>();

    @ViewChild(OtpCodeInputComponent) private _otp: OtpCodeInputComponent;

    /** The app-wide phone mask, also used as the placeholder — mirrors the Cardholders dialog. */
    protected readonly phoneNumberValidationMask = PHONE_VALIDATION_MASK;

    stage: 'phone' | 'code' | 'codes' = 'phone';
    recoveryCodes: string[] = [];
    codesAcknowledged = false;
    phoneForm: FormGroup<SmsPhoneForm>;
    code = '';
    isSending = false;
    isResending = false;
    isSaving = false;
    isSuccess = false;
    hasError = false;

    private _errorTimer: ReturnType<typeof setTimeout> | null = null;
    private _successTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(
            private _fb: NonNullableFormBuilder,
            private _userSettings: UserSettingsService,
            private _growler: GrowlerService,
            private _cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.phoneForm = this._fb.group({
            phone: ['', [Validators.required, Validators.minLength(10)]],
        });
    }

    /** Send the SMS code, then advance to the verify stage. */
    next(): void {
        if (this.phoneForm.invalid) {
            this.phoneForm.controls.phone.markAsTouched();
            this._growler.error('Error', 'Please enter a valid phone number.');
            return;
        }

        this.isSending = true;
        this._cdr.markForCheck();

        this._userSettings.startSmsAuth(this.phoneForm.controls.phone.value).pipe(untilDestroyed(this)).subscribe({
            next: () => {
                this.isSending = false;
                this.stage = 'code';
                this._cdr.markForCheck();
            },
            error: () => {
                this.isSending = false;
                this._cdr.markForCheck();
                this._growler.error('Error', 'We couldn\'t send a code to that number. Please check it and try again.');
            },
        });
    }

    /** Re-send the SMS code to the same number. */
    resendCode(): void {
        if (this.isResending) {
            return;
        }

        this.isResending = true;
        this._cdr.markForCheck();

        this._userSettings.startSmsAuth(this.phoneForm.controls.phone.value).pipe(untilDestroyed(this)).subscribe({
            next: () => {
                this.isResending = false;
                this._cdr.markForCheck();
                this._growler.success('Code sent', 'We\'ve sent a new code to your phone.');
            },
            error: () => {
                this.isResending = false;
                this._cdr.markForCheck();
                this._growler.error('Error', 'We couldn\'t resend the code. Please try again.');
            },
        });
    }

    onCodeChange(code: string): void {
        this.code = code;
    }

    verify(code: string): void {
        if (code.length !== 6 || this.isSaving || this.isSuccess) {
            return;
        }

        this.isSaving = true;
        this._cdr.markForCheck();

        this._userSettings.finishSmsSetup(code, this.phoneForm.controls.phone.value).pipe(
            finalize(() => this.isSaving = false),
            untilDestroyed(this),
        ).subscribe({
            next: (response: RecoveryCodesResponse) => {
                this.isSuccess = true;
                this._cdr.markForCheck();
                this._growler.success('Success', 'SMS has been enabled as a two-factor verification method on your account.');
                this._successTimer = setTimeout(() => this._finish(response?.recoveryCodes ?? []), 800);
            },
            error: () => {
                this._triggerError();
            },
        });
    }

    /**
     * Finish enrollment. When the server issued recovery codes (feature enabled, first method
     * enrolled), hand over to the show-once stage and hold completion until the user acknowledges
     * them; otherwise complete immediately. See google-auth-setup-step for why this renders in
     * place rather than opening a second dialog.
     */
    private _finish(recoveryCodes: string[]): void {
        if (recoveryCodes.length) {
            this.recoveryCodes = recoveryCodes;
            this.stage = 'codes';
            this.showingRecoveryCodes.emit();
            this._cdr.markForCheck();
            return;
        }
        this.completed.emit();
    }

    /** Show-once stage acknowledged — enrollment is complete. */
    onCodesAcknowledged(): void {
        if (!this.codesAcknowledged) {
            return;
        }
        this.completed.emit();
    }

    /** Back from the code stage returns to phone entry; from phone entry it backs out of the step. */
    onBack(): void {
        if (this.stage === 'code') {
            this.stage = 'phone';
            this.code = '';
            this._cdr.markForCheck();
            return;
        }
        this.cancelled.emit();
    }

    ngOnDestroy(): void {
        if (this._errorTimer) { clearTimeout(this._errorTimer); }
        if (this._successTimer) { clearTimeout(this._successTimer); }
    }

    private _triggerError(): void {
        this.hasError = true;
        this._cdr.markForCheck();
        this._growler.error('Error', 'We were unable to verify that code. Please try again.');
        this._errorTimer = setTimeout(() => {
            this.hasError = false;
            this.code = '';
            this._otp?.reset();
            this._cdr.markForCheck();
        }, 600);
    }
}
